import { BLANK_VALUE } from 'consts/ui'
import {
  LastMessageFilter,
  MessageAuthor,
  QueueStatsFilterDefinition,
  QueueStatsFilterRequest,
  QueueStatsFilterTypeDefinitionValue,
} from 'types'
import { formatBlankLabel } from 'utils/formatBlankLabel'

import { LAST_MESSAGE_AUTHOR_KEY, LAST_MESSAGE_DATE_KEY } from '../consts'
import {
  AdditionalFilterRecord,
  AdditionalFilterValue,
  ChildFilterData,
  OptionObject,
  PreparedFilter,
} from '../types'

import { DEFAULT_OPTION_KEY, getAllOptionsKey } from './getAllOptionsKey'
import {
  addParentSuffixToOption,
  getOptionAndSuffixDetails,
} from './getOptionAndSuffixDetails'

const sortWithBlankOnTop = (array: QueueStatsFilterTypeDefinitionValue[]) => {
  return [...array].sort((valA, valB) => {
    if (valA.id === BLANK_VALUE && valB.id !== BLANK_VALUE) {
      return -1
    }
    if (valA.id !== BLANK_VALUE && valB.id === BLANK_VALUE) {
      return 1
    }

    return 0
  })
}

const mapValuesToOptions = (
  values: QueueStatsFilterTypeDefinitionValue[],
  parentIdentifierForKeys: string,
): OptionObject[] =>
  sortWithBlankOnTop(values).map((value: QueueStatsFilterTypeDefinitionValue) => ({
    key: addParentSuffixToOption(value.id, parentIdentifierForKeys),
    value: addParentSuffixToOption(value.id, parentIdentifierForKeys),
    label: formatBlankLabel(value.label, value.id),
  }))

const splitFiltersToParentAndChild = (filters: QueueStatsFilterDefinition[]) => {
  const parentFiltersByKeyMap = new Map<string, QueueStatsFilterDefinition>()
  const childFiltersByParentKeyMap = new Map<string, ChildFilterData>()

  filters.forEach((item: QueueStatsFilterDefinition) => {
    if (item.parent) {
      const parentFilterKeyStr = item.parent.key
      const parentOptionId = item.parent.option

      if (!childFiltersByParentKeyMap.has(parentFilterKeyStr)) {
        childFiltersByParentKeyMap.set(parentFilterKeyStr, {
          subCategoryKey: item.key,
          optionsByParentId: new Map<string, QueueStatsFilterTypeDefinitionValue[]>(),
        })
      }

      const parentData = childFiltersByParentKeyMap.get(parentFilterKeyStr)
      if (parentData) {
        parentData.subCategoryKey = item.key
        parentData.optionsByParentId.set(parentOptionId, item.values)
      }
    } else {
      parentFiltersByKeyMap.set(item.key, item)
    }
  })

  return {
    parentFiltersByKeyMap,
    childFiltersByParentKeyMap,
  }
}

export const prepareFiltersArray = (
  filters: QueueStatsFilterDefinition[],
): PreparedFilter[] => {
  const resultFilters: PreparedFilter[] = []
  const { parentFiltersByKeyMap, childFiltersByParentKeyMap } =
    splitFiltersToParentAndChild(filters)

  parentFiltersByKeyMap.forEach(
    (parentDefinition: QueueStatsFilterDefinition, parentFilterKey: string) => {
      const currentTopLevelFilter: PreparedFilter = {
        key: parentFilterKey,
        label: parentDefinition.label,
        options: [],
      }

      const sortedTopLevelOptions = sortWithBlankOnTop(parentDefinition.values)

      sortedTopLevelOptions.forEach((option: QueueStatsFilterTypeDefinitionValue) => {
        const childFilterData = childFiltersByParentKeyMap.get(parentFilterKey)
        const childValuesForThisOption = childFilterData?.optionsByParentId.get(option.id)

        if (
          childValuesForThisOption &&
          childValuesForThisOption.length > 0 &&
          childFilterData
        ) {
          currentTopLevelFilter.options.push({
            key: addParentSuffixToOption(option.id, parentFilterKey),
            subCategoryKey: childFilterData.subCategoryKey ?? option.id,
            label: formatBlankLabel(option.label, option.id),
            children: mapValuesToOptions(childValuesForThisOption, option.id),
          })
        } else {
          currentTopLevelFilter.options.push({
            key: addParentSuffixToOption(option.id, parentFilterKey),
            label: formatBlankLabel(option.label, option.id),
            value: option.id,
          })
        }
      })

      resultFilters.push(currentTopLevelFilter)
    },
  )

  return resultFilters
}

// this is needed to optimise BE response
export const convertAllFullySelectedFilterValuesToEmptyArray = (
  filters: QueueStatsFilterRequest,
  allOptionsMap: Map<string, QueueStatsFilterTypeDefinitionValue[]>,
) => {
  const optimizedFilters: QueueStatsFilterRequest = {}

  Object.entries(filters).forEach(([filterKey, filterValues]) => {
    optimizedFilters[filterKey] = {}

    Object.entries(filterValues).forEach(([optionKey, optionValue]) => {
      const mapKey = getAllOptionsKey(filterKey, optionKey)
      const allOptions = allOptionsMap.get(mapKey)

      if (
        allOptions &&
        Array.isArray(optionValue) &&
        optionValue.length === allOptions.length
      ) {
        optimizedFilters[filterKey][optionKey] = []
      } else {
        optimizedFilters[filterKey][optionKey] = optionValue
      }
    })
  })

  return optimizedFilters
}

const prepareLastMessageSentFilter = ({
  lastMessageDate,
  lastMessageAuthor,
}: {
  lastMessageDate: AdditionalFilterValue
  lastMessageAuthor: AdditionalFilterValue
}): LastMessageFilter | undefined => {
  if (
    Array.isArray(lastMessageDate) ||
    typeof lastMessageDate !== 'object' ||
    !Array.isArray(lastMessageAuthor)
  ) {
    return undefined
  }

  return {
    sentAt: prepareFiltersForRequest(lastMessageDate),
    authors: lastMessageAuthor as MessageAuthor[],
  }
}

export const prepareFiltersForRequest = (
  filters: AdditionalFilterRecord,
): QueueStatsFilterRequest => {
  const {
    [LAST_MESSAGE_DATE_KEY]: lastMessageDate,
    [LAST_MESSAGE_AUTHOR_KEY]: lastMessageAuthor,
    ...restFilters
  } = filters

  const lastMessageFilter = prepareLastMessageSentFilter({
    lastMessageDate,
    lastMessageAuthor,
  })

  const restPreparedFilters = Object.entries(restFilters).reduce<QueueStatsFilterRequest>(
    (acc, [key, values]) => {
      if (Array.isArray(values)) {
        values.forEach(value => {
          const { suffix, pureOption } = getOptionAndSuffixDetails(value)
          const valueKey = suffix || DEFAULT_OPTION_KEY

          if (!acc[key]) {
            acc[key] = {}
          }
          if (!acc[key][valueKey]) {
            acc[key][valueKey] = []
          }

          if (Array.isArray(acc[key][valueKey])) {
            acc[key][valueKey].push(pureOption)
          }
        })
      } else if (typeof values === 'number') {
        acc[key] = { value: values }
      } else {
        acc[key] = prepareFiltersForRequest(values)
      }
      return acc
    },
    {},
  )

  if (lastMessageFilter) {
    restPreparedFilters.lastMessage = lastMessageFilter
  }

  return restPreparedFilters
}
